"use client";

import type React from "react";

import { useState, useMemo, useEffect, useCallback, useTransition } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Popover,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  ButtonBase,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  ExpandLess,
  Refresh,
  Cached,
  PendingActions,
  CheckCircleOutline,
  ArchiveOutlined,
} from "@mui/icons-material";
import { startOfWeek, addDays, subDays } from "date-fns";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import RsuiteDateRangePickerWrapper from "components/common/global-style/GlobalStyleDatepicker";
import { TechnicianRow } from "./technician-row";
import { MonthOverview } from "./month-overview";
import { DayOverview } from "./day-overview";
import { WEEK_DAYS_MON_FRI } from "./lib/utils/constants";
import type { ScheduledItem } from "./lib/types";
import type { WeekDayRange } from "./lib/types";
import {
  isLocationOpen,
  isHoliday,
  getTechnicianDayCapacity,
} from "./lib/utils/capacity-calculator";
import {
  DAY_LABELS,
  getDateForDayWithOffset,
  getDateString,
  getDayOfWeek,
  isSameLocalCalendarDay,
} from "./lib/utils/schedular-utils";
import {
  capacityBoardHeaderStyles,
  capacityBoardTitleBarStyles,
  weekNavigationButtonStyles,
  resetFiltersButtonStyles,
  dayHeaderPaperStyles,
  stickyHeaderRowStyles,
  scheduleItemViewFilterContainerStyles,
  scheduleItemViewFilterLabelStyles,
  scheduleItemViewFilterGroupStyles,
  scheduleItemViewFilterOptionStyles,
  weekDayRangeFilterOptionStyles,
  capacityBoardFilterControlStyles,
  capacityBoardToolbarRowStyles,
  capacityBoardFilterGroupStyles,
  capacityBoardWeekNavGroupStyles,
} from "./schedular-styles";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import { useTechnicianCalendarStore } from "../store/zustandTechnicianCalendarStore";
import type {
  SettingsLocation,
  ApplicationUser,
} from "../../models/lookupTypes";
import {
  actionIconButton,
  dropdownMenuProps,
} from "components/QueueBoard/filter-panel/FilterPanelStyles";
import localStorageHelper from "app/localStorage";
import { GetActiveInactiveTabColorStyleForCalendar } from "components/QueueBoard/models/workOrderStyles";
import {
  fadeInUp,
  scaleIn,
} from "components/common/global-style/GlobalStyling";

const WEEK_DAY_RANGE_OPTIONS: { value: WeekDayRange; label: string }[] = [
  { value: "sun-sat", label: "Sun–Sat" },
  { value: "mon-fri", label: "Mon–Fri" },
];

const SCHEDULE_ITEM_VIEW_OPTIONS = [
  { value: "active" as const, label: "Active", Icon: PendingActions },
  {
    value: "completed" as const,
    label: "Completed",
    Icon: CheckCircleOutline,
  },
  //{ value: "archived" as const, label: "Removed", Icon: ArchiveOutlined },
];

// Performance tracking - render counter
//let capacityBoardRenderCount = 0;

interface CapacityBoardProps {
  technicians: ApplicationUser[];
  days: readonly string[];
  scheduledItems: ScheduledItem[];
  availableHours: (techId: number, day: string, date?: Date) => number;
  getBaseCapacity: (techId: number, day: string, date?: Date) => number;
  getScheduledItemsForTechDay: (techId: number, day: string) => ScheduledItem[];
  workOrders: any[];
  segmentParts: any[];
  locations?: any[]; // Optional - will use cache if not provided
  businessHours: any[];
  holidays: any[];
  employeeSchedules: any[];
  employeeScheduleExceptions: any[];
  darkMode?: boolean;
  onThemeToggle?: () => void;
  onItemRemove: (itemId: string) => void | Promise<void>;
  onItemsRemove?: (itemIds: string[]) => void | Promise<void>;
  onItemComplete?: (itemId: string) => Promise<void>;
  onDropZone: (
    techId: number,
    day: string,
    date?: Date,
  ) => (e: React.DragEvent) => void;
  onDropAtPosition?: (
    techId: number,
    day: string,
    date?: Date,
  ) => (e: React.DragEvent, position: number) => void;
  onReorderItems?: (
    techId: number,
    day: string,
    fromItemId: string,
    toSortKey: number,
  ) => void;
  onUpdateItemHours?: (itemId: string, newHours: number) => Promise<void>;
  onResetFilters?: () => void;
  onResetScheduleCache?: () => void | Promise<void>;
  onLocationChange?: (locationIds: number[]) => void;
  onDayViewDateChange?: (date: Date) => void;
  onMonthViewDateChange?: (date: Date) => void;
  onViewModeChange?: (
    viewMode: "day" | "week" | "month",
    contextDate: Date,
  ) => void;
  onOpenWorkOrder?: (invoiceDocId: number) => void;
  onOpenSmartWo?: (technicianId: number, date: string) => void;
  triggerDayView?: number; // Increment to trigger switch to Day view
}

export function CapacityBoard({
  technicians: techniciansProp,
  days,
  scheduledItems,
  getBaseCapacity,
  getScheduledItemsForTechDay,
  workOrders,
  segmentParts,
  locations: locationsProp,
  businessHours,
  holidays,
  employeeSchedules,
  employeeScheduleExceptions,
  onItemRemove,
  onItemsRemove,
  onItemComplete,
  onDropZone,
  onDropAtPosition,
  onReorderItems,
  onUpdateItemHours,
  onResetFilters,
  onResetScheduleCache,
  onLocationChange,
  onDayViewDateChange,
  onMonthViewDateChange,
  onViewModeChange,
  onOpenWorkOrder,
  onOpenSmartWo,
  triggerDayView,
}: CapacityBoardProps) {
  const theme = useTheme();
  const {
    locations: locationsFromWorkOrderStore,
    techniciansForFilters: techniciansFromWorkOrderStore,
    woStatuses,
    woDocStatuses,
  } = useWorkOrderStore();

  // Use technician calendar store for dropdown data and filter state only
  // Scheduling data (businessHours, holidays, employeeSchedules, etc.) comes from props via schedule.state
  const locationsFromStore = useTechnicianCalendarStore((s) => s.locations);
  const techniciansFromStore = useTechnicianCalendarStore(
    (s) => s.techniciansForFilters,
  );
  const selectedLocationIds = useTechnicianCalendarStore(
    (s) => s.selectedLocationIds,
  );
  const selectedTechnicianIds = useTechnicianCalendarStore(
    (s) => s.selectedTechnicianIds,
  );
  const selectedWoStatusIds = useTechnicianCalendarStore(
    (s) => s.selectedWoStatusIds,
  );
  const selectedWoDocStatusIds = useTechnicianCalendarStore(
    (s) => s.selectedWoDocStatusIds,
  );
  const weekOffset = useTechnicianCalendarStore((s) => s.weekOffset);
  const scheduleItemView = useTechnicianCalendarStore((s) => s.scheduleItemView);
  const collapseScheduleItems = useTechnicianCalendarStore(
    (s) => s.collapseScheduleItems,
  );
  const weekDayRange = useTechnicianCalendarStore((s) => s.weekDayRange);
  const setLocations = useTechnicianCalendarStore((s) => s.setLocations);
  const setTechniciansForFilters = useTechnicianCalendarStore(
    (s) => s.setTechniciansForFilters,
  );
  const setSelectedLocationIds = useTechnicianCalendarStore(
    (s) => s.setSelectedLocationIds,
  );
  const setSelectedTechnicianIds = useTechnicianCalendarStore(
    (s) => s.setSelectedTechnicianIds,
  );
  const setSelectedWoStatusIds = useTechnicianCalendarStore(
    (s) => s.setSelectedWoStatusIds,
  );
  const setSelectedWoDocStatusIds = useTechnicianCalendarStore(
    (s) => s.setSelectedWoDocStatusIds,
  );
  const setWeekOffset = useTechnicianCalendarStore((s) => s.setWeekOffset);
  const setScheduleItemView = useTechnicianCalendarStore(
    (s) => s.setScheduleItemView,
  );
  const setCollapseScheduleItems = useTechnicianCalendarStore(
    (s) => s.setCollapseScheduleItems,
  );
  const setWeekDayRange = useTechnicianCalendarStore((s) => s.setWeekDayRange);

  const isScheduleReadOnly = scheduleItemView !== "active";
  const [, startCollapseTransition] = useTransition();
  const visibleWeekDays = useMemo(
    () =>
      weekDayRange === "mon-fri"
        ? days.filter((day) =>
            (WEEK_DAYS_MON_FRI as readonly string[]).includes(day),
          )
        : days,
    [days, weekDayRange],
  );
  const [isResettingScheduleCache, setIsResettingScheduleCache] =
    useState(false);

  const handleResetScheduleCache = useCallback(async () => {
    if (!onResetScheduleCache || isResettingScheduleCache) {
      return;
    }
    setIsResettingScheduleCache(true);
    try {
      await onResetScheduleCache();
    } finally {
      setIsResettingScheduleCache(false);
    }
  }, [onResetScheduleCache, isResettingScheduleCache]);

  // Local state for view mode (day/week/month) - NOT persisted to avoid cascading re-renders
  // This follows the same pattern as WorkOrderDialogContainerMain for fast tab switching
  const [viewMode, setViewMode] = useState<"day" | "week" | "month">("week");

  // Watch for triggerDayView changes to switch to Day view programmatically
  useEffect(() => {
    if (triggerDayView && triggerDayView > 0) {
      const today = new Date();
      setViewMode("day");
      setDaySelectedDate(today);
      onViewModeChange?.("day", today);
      onDayViewDateChange?.(today);
    }
  }, [triggerDayView]);

  // Performance tracking
  //capacityBoardRenderCount++;
  // console.log(`[WEEK TAB] CapacityBoard render #${capacityBoardRenderCount}`, {
  //   viewMode,
  //   techniciansCount: techniciansProp.length,
  //   scheduledItemsCount: scheduledItems.length,
  // });

  // Sync dropdown data from work order store when it updates (but prefer store data if available)
  useEffect(() => {
    // Only update if work order store has data and our store is empty or has less data
    if (
      locationsFromWorkOrderStore.length > 0 &&
      (locationsFromStore.length === 0 ||
        locationsFromWorkOrderStore.length > locationsFromStore.length)
    ) {
      setLocations(locationsFromWorkOrderStore);
    }
  }, [locationsFromWorkOrderStore, locationsFromStore.length, setLocations]);

  useEffect(() => {
    // Only update if work order store has data and our store is empty or has less data
    if (
      techniciansFromWorkOrderStore.length > 0 &&
      (techniciansFromStore.length === 0 ||
        techniciansFromWorkOrderStore.length > techniciansFromStore.length)
    ) {
      setTechniciansForFilters(techniciansFromWorkOrderStore);
    }
  }, [
    techniciansFromWorkOrderStore,
    techniciansFromStore.length,
    setTechniciansForFilters,
  ]);

  // Use prop data if provided, otherwise use store data, fallback to work order store
  // Map Location (identity) to expected format (locationId)
  const locations = useMemo(() => {
    if (locationsProp && locationsProp.length > 0) {
      return locationsProp;
    }
    // Prefer store data (from cache), fallback to work order store
    const sourceLocations =
      locationsFromStore.length > 0
        ? locationsFromStore
        : locationsFromWorkOrderStore;
    return sourceLocations.map((loc: SettingsLocation) => ({
      identity: loc.identity,
      displayText: loc.displayText,
      companyName: loc.companyName,
      glNo: loc.GeneralLedgerNumber,
      emailAddr: loc.emailAddress,
      taxGroupId: loc.taxGroupId,
      taxExemptId: loc.taxExemptId,
    }));
  }, [locationsProp, locationsFromStore, locationsFromWorkOrderStore]);

  // Get user's default location for initial selection
  const getUserDefaultLocation = (): number | null => {
    const user = localStorageHelper.getByKey("user") as
      | ApplicationUser
      | undefined;
    return user?.defaultLocationIdentity ?? null;
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Keep dates independent per tab to avoid cross-tab coupling
  const [weekSelectedDate, setWeekSelectedDate] = useState<Date>(new Date());
  const [daySelectedDate, setDaySelectedDate] = useState<Date>(new Date());
  const [monthSelectedDate, setMonthSelectedDate] = useState<Date>(new Date());

  // Initialize Locations dropdown to "All" when locations first become available
  useEffect(() => {
    if (locations.length === 0) return;

    // Default to "All" when nothing is selected yet
    if (selectedLocationIds.length === 0) {
      setSelectedLocationIds(["0"]);
    }

    // Convert selectedLocationIds to number array for onLocationChange
    // "0" means "All" - in that case pass empty array to fetch all
    const locationIdsForApi = selectedLocationIds.includes("0")
      ? []
      : selectedLocationIds
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));

    onLocationChange?.(locationIdsForApi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations.length]); // Only run when locations first become available

  // Map technicians to Technician format and filter by selected locations
  // Use string IDs to match FilterPanel pattern
  // Prefer store data (from cache), fallback to work order store
  const filteredTechniciansForDropdown = useMemo(() => {
    const sourceTechnicians =
      techniciansFromStore.length > 0
        ? techniciansFromStore
        : techniciansFromWorkOrderStore;

    let techs = sourceTechnicians.map((tech: ApplicationUser) => ({
      id: String(tech.identity), // Use string IDs to match FilterPanel pattern
      name: tech.userName,
      defaultLocationId: tech.defaultLocationIdentity,
    }));

    // Filter by selected locations if specific locations are selected (not "All")
    if (!selectedLocationIds.includes("0") && selectedLocationIds.length > 0) {
      const locationIdNumbers = selectedLocationIds
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
      techs = techs.filter((tech) =>
        locationIdNumbers.includes(tech.defaultLocationId),
      );
    }

    return techs;
  }, [
    techniciansFromStore,
    techniciansFromWorkOrderStore,
    selectedLocationIds,
  ]);
  const [headerExpanded, setHeaderExpanded] = useState(true); // Start expanded
  const calendarOpen = Boolean(anchorEl);

  // Scheduling data comes directly from props (via schedule.state in page.tsx)
  // This is the single source of truth for capacity calculations
  const businessHoursToUse = businessHours;
  const holidaysToUse = holidays;
  const employeeSchedulesToUse = employeeSchedules;
  const employeeScheduleExceptionsToUse = employeeScheduleExceptions;

  // Filter data arrays by selected location for use in calculations (no memoization)

  // Wrapper functions that use filtered data for capacity calculations
  // These ensure capacity calculations use data filtered by selected location
  const getBaseCapacityWithFilter = useCallback(
    (techId: number, day: string, date?: Date): number => {
      // Debug breakpoint: base capacity with location filter
      // Hits whenever capacity is calculated for a technician/day using filtered data
      // (including on first load and page refresh).
      //debugger;

      const tech = techniciansProp.find((t) => t.identity === techId);
      if (!tech) return 0;

      // Always use technician's own default location for capacity calculations
      // This ensures each technician's capacity is based on their location's schedule
      const locationIdToUse = tech.defaultLocationIdentity;

      // Filter schedules and exceptions for this technician's location
      const techSchedules = employeeSchedulesToUse.filter(
        (es: any) =>
          es.appUserId === tech.identity && es.locationId === locationIdToUse,
      );
      const techExceptions = employeeScheduleExceptionsToUse.filter(
        (exc: any) =>
          exc.appUserId === tech.identity && exc.locationId === locationIdToUse,
      );

      const dayOfWeekMap: { [key: string]: number } = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };
      const dayOfWeek = dayOfWeekMap[day] ?? 1;

      if (date) {
        return getTechnicianDayCapacity(
          tech.identity,
          dayOfWeek,
          date,
          locationIdToUse,
          techSchedules,
          techExceptions,
        );
      } else {
        // Fallback: find schedule for this day
        const schedule = techSchedules.find(
          (es: any) => es.dayOfWeek === dayOfWeek,
        );
        if (schedule && schedule.beginTime && schedule.endTime) {
          // Handle both Date objects and date strings
          const begin =
            schedule.beginTime instanceof Date
              ? schedule.beginTime
              : new Date(schedule.beginTime);
          const end =
            schedule.endTime instanceof Date
              ? schedule.endTime
              : new Date(schedule.endTime);

          if (isNaN(begin.getTime()) || isNaN(end.getTime())) {
            return 0;
          }

          const diffMs = end.getTime() - begin.getTime();
          return diffMs / (1000 * 60 * 60);
        }
      }
      return 0;
    },
    [
      techniciansProp,
      employeeSchedulesToUse,
      employeeScheduleExceptionsToUse,
      viewMode, // CRITICAL: Recalculate when view mode changes to ensure fresh data is used
    ],
  );

  const scheduledItemsIndex = useMemo(() => {
    const index = new Map<string, ScheduledItem[]>();
    for (const item of scheduledItems) {
      const key = `${item.technicianId}:${item.day}`;
      const list = index.get(key) ?? [];
      list.push(item);
      index.set(key, list);
    }
    return index;
  }, [scheduledItems]);

  const getAvailableHoursWithFilter = useCallback(
    (techId: number, day: string, date?: Date): number => {
      const tech = techniciansProp.find((t) => t.identity === techId);
      if (!tech) return 0;

      const scheduled = (scheduledItemsIndex.get(`${techId}:${day}`) ?? []).filter(
        (item) =>
          !date ||
          !item.workDate ||
          isSameLocalCalendarDay(item.workDate, date),
      );
      const used = scheduled.reduce((sum, item) => sum + item.hours, 0);
      const baseCapacity = getBaseCapacityWithFilter(techId, day, date);

      return Math.max(0, baseCapacity - used);
    },
    [techniciansProp, scheduledItemsIndex, getBaseCapacityWithFilter, viewMode],
  );

  // Filter technicians based on selected location and technician filter
  const filteredTechnicians = useMemo(() => {
    return techniciansProp.filter((tech) => {
      // Filter by selected technician IDs ("0" or empty array means all)
      if (
        selectedTechnicianIds.length > 0 &&
        !selectedTechnicianIds.includes("0") &&
        !selectedTechnicianIds.includes(String(tech.identity))
      )
        return false;

      // Filter by selected locations ("0" means all)
      if (
        !selectedLocationIds.includes("0") &&
        selectedLocationIds.length > 0
      ) {
        const locationIdNumbers = selectedLocationIds
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
        if (!locationIdNumbers.includes(tech.defaultLocationIdentity)) {
          return false;
        }
      }

      return true;
    });
  }, [techniciansProp, selectedLocationIds, selectedTechnicianIds]);

  // Using utility functions from schedular-utils

  const dates: { [key: string]: string } = {
    sunday: getDateString("sunday", weekOffset),
    monday: getDateString("monday", weekOffset),
    tuesday: getDateString("tuesday", weekOffset),
    wednesday: getDateString("wednesday", weekOffset),
    thursday: getDateString("thursday", weekOffset),
    friday: getDateString("friday", weekOffset),
    saturday: getDateString("saturday", weekOffset),
  };

  const dateObjects: { [key: string]: Date } = {
    sunday: getDateForDayWithOffset("sunday", weekOffset),
    monday: getDateForDayWithOffset("monday", weekOffset),
    tuesday: getDateForDayWithOffset("tuesday", weekOffset),
    wednesday: getDateForDayWithOffset("wednesday", weekOffset),
    thursday: getDateForDayWithOffset("thursday", weekOffset),
    friday: getDateForDayWithOffset("friday", weekOffset),
    saturday: getDateForDayWithOffset("saturday", weekOffset),
  };

  const techDayRowData = useMemo(() => {
    type TechDayData = {
      scheduledItems: Record<string, ScheduledItem[]>;
      availableHours: Record<string, number>;
      baseCapacity: Record<string, number>;
      locationClosed: Record<string, boolean>;
      technicianNotWorking: Record<string, boolean>;
    };

    const rowData = new Map<number, TechDayData>();

    for (const tech of filteredTechnicians) {
      const scheduledItemsByDay: Record<string, ScheduledItem[]> = {};
      const availableHoursByDay: Record<string, number> = {};
      const baseCapacityByDay: Record<string, number> = {};
      const locationClosedMap: Record<string, boolean> = {};
      const technicianNotWorkingMap: Record<string, boolean> = {};

      for (const day of days) {
        const date = dateObjects[day];
        const dayOfWeek = getDayOfWeek(day);
        // Filter by workDate so items from another week (same weekday) do not appear
        scheduledItemsByDay[day] = getScheduledItemsForTechDay(
          tech.identity,
          day,
        ).filter(
          (item) =>
            !item.workDate || isSameLocalCalendarDay(item.workDate, date),
        );
        availableHoursByDay[day] = getAvailableHoursWithFilter(
          tech.identity,
          day,
          date,
        );
        baseCapacityByDay[day] = getBaseCapacityWithFilter(
          tech.identity,
          day,
          date,
        );

        const techLocationId = tech.defaultLocationIdentity;
        const locationIdsToCheck = [techLocationId];
        const holidaysFiltered = holidaysToUse.filter(
          (h: any) => h.locationId === techLocationId,
        );
        const businessHoursFiltered = businessHoursToUse.filter(
          (bh: any) =>
            bh.locationId === techLocationId && bh.dayOfWeek === dayOfWeek,
        );
        const hasBusinessHours = businessHoursToUse.some(
          (bh: any) =>
            bh.locationId === techLocationId && bh.dayOfWeek === dayOfWeek,
        );
        const isLocationClosedByHours = !hasBusinessHours;
        const isHolidayDay = isHoliday(
          date,
          locationIdsToCheck,
          holidaysFiltered,
        );
        const isOpen = isLocationOpen(
          dayOfWeek,
          locationIdsToCheck,
          businessHoursFiltered,
        );
        locationClosedMap[day] =
          isLocationClosedByHours || isHolidayDay || !isOpen;

        // "Off" is only a full-day leave exception, not a blank weekly schedule row
        const hasFullDayException = employeeScheduleExceptionsToUse.some(
          (exc: any) =>
            exc.appUserId === tech.identity &&
            exc.locationId === techLocationId &&
            isSameLocalCalendarDay(exc.exceptionDate, date) &&
            !exc.beginTime &&
            !exc.endTime,
        );
        technicianNotWorkingMap[day] = hasFullDayException;
      }

      rowData.set(tech.identity, {
        scheduledItems: scheduledItemsByDay,
        availableHours: availableHoursByDay,
        baseCapacity: baseCapacityByDay,
        locationClosed: locationClosedMap,
        technicianNotWorking: technicianNotWorkingMap,
      });
    }

    return rowData;
  }, [
    filteredTechnicians,
    days,
    dateObjects,
    getScheduledItemsForTechDay,
    getAvailableHoursWithFilter,
    getBaseCapacityWithFilter,
    holidaysToUse,
    businessHoursToUse,
    employeeScheduleExceptionsToUse,
  ]);

  // Calculate current week's Sunday as base (week starts on Sunday to match EmployeeSchedule)
  const currentDate = new Date();
  const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 0 });

  // Calculate week start based on offset from current week
  const weekStart = new Date(currentWeekStart);
  weekStart.setDate(currentWeekStart.getDate() + weekOffset * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6); // End on Saturday (6 days after Sunday)

  // Calculate if current week is being displayed
  const isCurrentWeek = weekOffset === 0;

  // Function to navigate to current week
  const goToCurrentWeek = () => {
    setWeekOffset(0);
    setWeekSelectedDate(new Date());
  };

  // Handle reset filters
  const handleResetFilters = () => {
    // Reset locations to "All" (always, not user's default)
    setSelectedLocationIds(["0"]);

    // Reset technicians to "All" ("0")
    setSelectedTechnicianIds(["0"]);
    // Reset WO statuses to "All" ("0")
    setSelectedWoStatusIds(["0"]);
    setSelectedWoDocStatusIds(["0"]);
    // Reset week to current week (set directly, don't use goToCurrentWeek to avoid extra state updates)
    setWeekOffset(0);
    setWeekSelectedDate(new Date());
    // Reset view mode to week
    setViewMode("week");

    // IMPORTANT: Only call onResetFilters which handles ALL fetching with correct parameters
    // Do NOT call onLocationChange separately to avoid duplicate API calls
    // The onResetFilters callback in page.tsx will:
    // 1. Read the latest store values (which we just set above)
    // 2. Fetch work orders and scheduled items with the correct "All" locations and weekOffset=0
    onResetFilters?.();
  };

  // Listen for refresh events from Smart Work Order Creation Dialog
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleRefreshWorkOrders = () => {
      handleResetFilters();
    };

    window.addEventListener("refreshWorkOrdersTechCalendar", handleRefreshWorkOrders);

    return () => {
      window.removeEventListener("refreshWorkOrdersTechCalendar", handleRefreshWorkOrders);
    };
  }, []); // Empty deps - listener only needs to be set up once, handleResetFilters is stable

  // Day view navigation handlers
  const handlePreviousDay = () => {
    const newDate = subDays(daySelectedDate, 1);
    setDaySelectedDate(newDate);
    onDayViewDateChange?.(newDate);
  };

  const handleNextDay = () => {
    const newDate = addDays(daySelectedDate, 1);
    setDaySelectedDate(newDate);
    onDayViewDateChange?.(newDate);
  };

  const handleDayDateChange = (date: Date) => {
    setDaySelectedDate(date);
    onDayViewDateChange?.(date);
  };

  // Handle date selection from calendar
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    setWeekSelectedDate(date);

    // Find the Sunday of the selected week (week starts on Sunday to match EmployeeSchedule)
    const selectedSunday = startOfWeek(date, { weekStartsOn: 0 });

    // Calculate offset from current week's Sunday
    const currentWeekSunday = startOfWeek(new Date(), { weekStartsOn: 0 });
    const diffTime = selectedSunday.getTime() - currentWeekSunday.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    const offset = Math.floor(diffDays / 7);

    setWeekOffset(offset);
    setAnchorEl(null); // Close popover
  };

  // Handle date selection specifically for Day view calendar
  // This triggers a fetch for scheduled items for the selected day
  // NOTE: We do NOT update weekOffset here to avoid triggering the week fetch useEffect
  // Day view has its own independent date selection
  const handleDayViewDateChange = (date: Date | undefined) => {
    if (!date) return;

    setDaySelectedDate(date);

    // Notify parent to fetch scheduled items for this specific day
    // NOTE: Removed setWeekOffset() call - Day view should not affect Week view's offset
    // This prevents duplicate API calls (one for day, one for week)
    onDayViewDateChange?.(date);
  };

  // Handle date selection specifically for Month view
  // This triggers a fetch for scheduled items for the selected month
  const handleMonthViewDateChange = (date: Date | undefined) => {
    if (!date) return;

    setMonthSelectedDate(date);

    // Notify parent to fetch scheduled items for this month
    onMonthViewDateChange?.(date);
  };

  const handleOpenCalendar = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setAnchorEl(null);
  };

  const formatDateForInput = (date: Date) => {
    // yyyy-mm-dd for native <input type="date" />
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseDateFromInput = (value: string) => {
    // value is yyyy-mm-dd
    if (!value) return undefined;
    const [yyyy, mm, dd] = value.split("-").map((x) => Number(x));
    if (!yyyy || !mm || !dd) return undefined;
    const date = new Date(yyyy, mm - 1, dd);
    return Number.isNaN(date.getTime()) ? undefined : date;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "fit-content", // Allow width to be determined by content
        minWidth: "100%", // At minimum, take full parent width
        flexShrink: 0,
        "& > *": {
          minWidth: "1200px", // Ensure children have minimum width for scroll trigger
        },
        animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
      }}
    >
      {/* Header with title and week navigation */}
      <Paper elevation={2} square sx={capacityBoardHeaderStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            width: "100%",
          }}
        >
          <Box
            sx={capacityBoardTitleBarStyles}
            onClick={() => setHeaderExpanded(!headerExpanded)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setHeaderExpanded(!headerExpanded);
                }}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.15)"
                        : "rgba(255, 255, 255, 0.1)",
                  },
                }}
                title={headerExpanded ? "Collapse filters" : "Expand filters"}
              >
                {headerExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "white" }}>
                Service Calendar V2
              </Typography>
            </Box>
          </Box>

          <Collapse in={headerExpanded}>
            <Tabs
              value={viewMode}
              onChange={(_, value) => {
                setViewMode(value);

                // Keep each tab independent:
                // - Day: always defaults to TODAY when entering Day tab
                // - Week: defaults to current week when entering Week tab
                // - Month: keeps last selected month
                if (value === "day") {
                  const today = new Date();
                  setDaySelectedDate(today);
                  onViewModeChange?.("day", today);
                } else if (value === "week") {
                  const today = new Date();
                  setWeekOffset(0);
                  setWeekSelectedDate(today);
                  onViewModeChange?.("week", today);
                } else if (value === "month") {
                  onViewModeChange?.("month", monthSelectedDate);
                }
              }}
              textColor="inherit"
              indicatorColor="primary"
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              <Tab
                sx={{
                  ...GetActiveInactiveTabColorStyleForCalendar(theme),
                }}
                value="day"
                label="Day"
              />
              <Tab
                sx={{
                  ...GetActiveInactiveTabColorStyleForCalendar(theme),
                }}
                value="week"
                label="Week"
              />
              <Tab
                sx={{
                  ...GetActiveInactiveTabColorStyleForCalendar(theme),
                }}
                value="month"
                label="Month"
              />
            </Tabs>

            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                m: 1,
                px: 1.5,
                py: 1.5,
                borderRadius: "8px",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 4px 24px rgba(0,0,0,0.3)"
                    : "0 4px 24px rgba(0,0,0,0.06)",
                animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
              }}
            >
              <Box
                sx={{
                  ...capacityBoardToolbarRowStyles,
                  animation: `${scaleIn} 0.4s ease-out 0.4s both`,
                }}
              >
                <Box sx={capacityBoardFilterGroupStyles}>
                {/* Locations dropdown - Multi-select */}
                <FormControl size="small" sx={capacityBoardFilterControlStyles}>
                  <InputLabel shrink={true}>Locations</InputLabel>
                  <Select
                    MenuProps={dropdownMenuProps(theme)}
                    multiple
                    size="small"
                    value={selectedLocationIds}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      // Handle "0" (All) selection similar to Technicians dropdown
                      let newSelection: string[];
                      if (value.includes("0")) {
                        if (!selectedLocationIds.includes("0")) {
                          // If "0" is selected and wasn't before, set to ["0"]
                          newSelection = ["0"];
                        } else {
                          // If "0" was already selected, remove it and use filtered locations
                          const filtered = value.filter((v) => v !== "0");
                          newSelection = filtered.length > 0 ? filtered : ["0"];
                        }
                      } else {
                        // No "0" in selection
                        newSelection = value.length > 0 ? value : ["0"];
                      }

                      setSelectedLocationIds(newSelection);

                      // Convert to number array for onLocationChange
                      // "0" means "All" - in that case pass empty array to fetch all
                      const locationIdsForApi = newSelection.includes("0")
                        ? []
                        : newSelection
                            .map((id) => parseInt(id, 10))
                            .filter((id) => !isNaN(id));
                      onLocationChange?.(locationIdsForApi);

                      // Clear technician selection when location changes (reset to "All")
                      setSelectedTechnicianIds(["0"]);
                    }}
                    input={<OutlinedInput label="Locations" />}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.includes("0")) return "All";

                      const selectedLocations = locations.filter((l: any) =>
                        selected.includes(l.identity.toString()),
                      );

                      if (selectedLocations.length === 0) return "All";
                      if (selectedLocations.length === 1) {
                        return selectedLocations[0].displayText;
                      }
                      if (selectedLocations.length <= 2) {
                        return selectedLocations
                          .map((l: any) => l.displayText)
                          .join(", ");
                      }
                      return `${selectedLocations.length} locations selected`;
                    }}
                  >
                    {/* All option */}
                    <MenuItem value="0">
                      <Checkbox checked={selectedLocationIds.includes("0")} />
                      <ListItemText primary="All" />
                    </MenuItem>
                    {/* Individual locations */}
                    {locations.map((location: any) => (
                      <MenuItem
                        key={location.identity}
                        value={location.identity.toString()}
                      >
                        <Checkbox
                          checked={selectedLocationIds.includes(
                            location.identity.toString(),
                          )}
                        />
                        <ListItemText primary={location.displayText} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Technicians dropdown */}
                <FormControl size="small" sx={capacityBoardFilterControlStyles}>
                  <InputLabel shrink={true}>Technicians</InputLabel>
                  <Select
                    MenuProps={dropdownMenuProps(theme)}
                    multiple
                    size="small"
                    value={selectedTechnicianIds}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      // Handle "0" (All) selection similar to FilterPanel
                      if (value.includes("0")) {
                        if (!selectedTechnicianIds.includes("0")) {
                          // If "0" is selected and wasn't before, set to ["0"]
                          setSelectedTechnicianIds(["0"]);
                        } else {
                          // If "0" was already selected, remove it and use filtered techs
                          const filteredTechs = value.filter((v) => v !== "0");
                          setSelectedTechnicianIds(
                            filteredTechs.length > 0 ? filteredTechs : ["0"],
                          );
                        }
                      } else {
                        // No "0" in selection
                        if (value.length === 0) {
                          // If empty, default to "All"
                          setSelectedTechnicianIds(["0"]);
                        } else {
                          setSelectedTechnicianIds(value);
                        }
                      }
                    }}
                    input={<OutlinedInput label="Technicians" />}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.includes("0")) return "All";

                      const filteredTech =
                        filteredTechniciansForDropdown.filter((technician) =>
                          selected.includes(String(technician.id)),
                        );

                      if (filteredTech.length === 0) return "All";

                      // Show only first 2-3 names, then "..."
                      const maxDisplayNames = 2;
                      const displayNames = filteredTech
                        .slice(0, maxDisplayNames)
                        .map((technician) => technician.name);
                      const remainingCount =
                        filteredTech.length - maxDisplayNames;

                      const displayText =
                        remainingCount > 0
                          ? `${displayNames.join(", ")}...`
                          : displayNames.join(", ");

                      // Store all names for tooltip (one per line)
                      const allNames = filteredTech.map((tech) => tech.name);

                      // Only show tooltip if there are more names than displayed
                      if (remainingCount > 0) {
                        return (
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  "&::-webkit-scrollbar": {
                                    width: "6px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    bgcolor: "rgba(0, 0, 0, 0.1)",
                                    borderRadius: 1,
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    bgcolor: "rgba(0, 0, 0, 0.3)",
                                    borderRadius: 1,
                                    "&:hover": {
                                      bgcolor: "rgba(0, 0, 0, 0.5)",
                                    },
                                  },
                                }}
                              >
                                {allNames.map((name, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: "block",
                                      py: 0.25,
                                      color: "inherit",
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: "300px",
                                  bgcolor: "rgba(97, 97, 97, 0.95)",
                                },
                              },
                            }}
                          >
                            <span
                              style={{ display: "inline-block", width: "100%" }}
                            >
                              {displayText}
                            </span>
                          </Tooltip>
                        );
                      }

                      return displayText;
                    }}
                  >
                    <MenuItem
                      sx={{
                        height: "30px",
                        margin: "2px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              selectedTechnicianIds.length ===
                                filteredTechniciansForDropdown.length &&
                              filteredTechniciansForDropdown.length > 0 &&
                              !selectedTechnicianIds.includes("0")
                            }
                            indeterminate={
                              selectedTechnicianIds.length > 0 &&
                              selectedTechnicianIds.length <
                                filteredTechniciansForDropdown.length &&
                              !selectedTechnicianIds.includes("0")
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              if (
                                selectedTechnicianIds.length ===
                                  filteredTechniciansForDropdown.length &&
                                !selectedTechnicianIds.includes("0")
                              ) {
                                // Unselect all
                                setSelectedTechnicianIds(["0"]);
                              } else {
                                // Select all
                                const allIds =
                                  filteredTechniciansForDropdown.map((tech) =>
                                    String(tech.id),
                                  );
                                setSelectedTechnicianIds(allIds);
                              }
                            }}
                            sx={{
                              padding: "4px",
                            }}
                          />
                        }
                        label={
                          selectedTechnicianIds.length ===
                            filteredTechniciansForDropdown.length &&
                          filteredTechniciansForDropdown.length > 0 &&
                          !selectedTechnicianIds.includes("0")
                            ? "Unselect All"
                            : "Select All"
                        }
                        sx={{
                          margin: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "12px",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </MenuItem>
                    {filteredTechniciansForDropdown.map((tech) => (
                      <MenuItem
                        key={tech.id}
                        value={String(tech.id)}
                        sx={{
                          height: "30px",
                          margin: "2px",
                        }}
                      >
                        <Checkbox
                          checked={selectedTechnicianIds.includes(
                            String(tech.id),
                          )}
                        />
                        <ListItemText primary={tech.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Work Order Status dropdown */}
                <FormControl size="small" sx={capacityBoardFilterControlStyles}>
                  <InputLabel shrink={true}>WO Status</InputLabel>
                  <Select
                    MenuProps={dropdownMenuProps(theme)}
                    multiple
                    size="small"
                    value={selectedWoStatusIds}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      // Handle "0" (All) selection similar to FilterPanel
                      if (value.includes("0")) {
                        if (!selectedWoStatusIds.includes("0")) {
                          // If "0" is selected and wasn't before, set to ["0"]
                          setSelectedWoStatusIds(["0"]);
                        } else {
                          // If "0" was already selected, remove it and use filtered statuses
                          const filteredStatuses = value.filter(
                            (v) => v !== "0",
                          );
                          setSelectedWoStatusIds(
                            filteredStatuses.length > 0
                              ? filteredStatuses
                              : ["0"],
                          );
                        }
                      } else {
                        // No "0" in selection
                        if (value.length === 0) {
                          // If empty, default to "All"
                          setSelectedWoStatusIds(["0"]);
                        } else {
                          setSelectedWoStatusIds(value);
                        }
                      }
                    }}
                    input={<OutlinedInput label="WO Status" />}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.includes("0")) return "All";

                      const filteredStatuses = woStatuses.filter((status) =>
                        selected.includes(String(status.identity)),
                      );

                      if (filteredStatuses.length === 0) return "All";

                      // Show only first 2-3 names, then "..."
                      const maxDisplayNames = 2;
                      const displayNames = filteredStatuses
                        .slice(0, maxDisplayNames)
                        .map((status) => status.displayText);
                      const remainingCount =
                        filteredStatuses.length - maxDisplayNames;

                      const displayText =
                        remainingCount > 0
                          ? `${displayNames.join(", ")}...`
                          : displayNames.join(", ");

                      // Store all names for tooltip (one per line)
                      const allNames = filteredStatuses.map(
                        (status) => status.displayText,
                      );

                      // Only show tooltip if there are more names than displayed
                      if (remainingCount > 0) {
                        return (
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  "&::-webkit-scrollbar": {
                                    width: "6px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    bgcolor: "rgba(0, 0, 0, 0.1)",
                                    borderRadius: 1,
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    bgcolor: "rgba(0, 0, 0, 0.3)",
                                    borderRadius: 1,
                                    "&:hover": {
                                      bgcolor: "rgba(0, 0, 0, 0.5)",
                                    },
                                  },
                                }}
                              >
                                {allNames.map((name, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: "block",
                                      py: 0.25,
                                      color: "inherit",
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: "300px",
                                  bgcolor: "rgba(97, 97, 97, 0.95)",
                                },
                              },
                            }}
                          >
                            <span
                              style={{ display: "inline-block", width: "100%" }}
                            >
                              {displayText}
                            </span>
                          </Tooltip>
                        );
                      }

                      return displayText;
                    }}
                  >
                    <MenuItem
                      sx={{
                        height: "30px",
                        margin: "2px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              woStatuses.length ===
                                selectedWoStatusIds.length &&
                              woStatuses.length > 0 &&
                              !selectedWoStatusIds.includes("0")
                            }
                            indeterminate={
                              selectedWoStatusIds.length > 0 &&
                              selectedWoStatusIds.length < woStatuses.length &&
                              !selectedWoStatusIds.includes("0")
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              if (
                                woStatuses.length ===
                                  selectedWoStatusIds.length &&
                                !selectedWoStatusIds.includes("0")
                              ) {
                                // Unselect all
                                setSelectedWoStatusIds(["0"]);
                              } else {
                                // Select all
                                const allIds = woStatuses.map((status) =>
                                  String(status.identity),
                                );
                                setSelectedWoStatusIds(allIds);
                              }
                            }}
                            sx={{
                              padding: "4px",
                            }}
                          />
                        }
                        label={
                          woStatuses.length === selectedWoStatusIds.length &&
                          woStatuses.length > 0 &&
                          !selectedWoStatusIds.includes("0")
                            ? "Unselect All"
                            : "Select All"
                        }
                        sx={{
                          margin: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "12px",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </MenuItem>
                    {woStatuses.map((status) => (
                      <MenuItem
                        key={status.identity}
                        value={String(status.identity)}
                        sx={{
                          height: "30px",
                          margin: "2px",
                        }}
                      >
                        <Checkbox
                          checked={selectedWoStatusIds.includes(
                            String(status.identity),
                          )}
                        />
                        <ListItemText primary={status.displayText} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Document status (InvoiceHeader.Status) — same multi-select behavior as WO Status */}
                <FormControl size="small" sx={capacityBoardFilterControlStyles}>
                  <InputLabel shrink={true}>Doc Status</InputLabel>
                  <Select
                    MenuProps={dropdownMenuProps(theme)}
                    multiple
                    size="small"
                    value={selectedWoDocStatusIds}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      if (value.includes("0")) {
                        if (!selectedWoDocStatusIds.includes("0")) {
                          setSelectedWoDocStatusIds(["0"]);
                        } else {
                          const filtered = value.filter((v) => v !== "0");
                          setSelectedWoDocStatusIds(
                            filtered.length > 0 ? filtered : ["0"],
                          );
                        }
                      } else {
                        if (value.length === 0) {
                          setSelectedWoDocStatusIds(["0"]);
                        } else {
                          setSelectedWoDocStatusIds(value);
                        }
                      }
                    }}
                    input={<OutlinedInput label="Doc Status" />}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.includes("0")) return "All";
                      const filtered = woDocStatuses.filter((s) =>
                        selected.includes(s),
                      );
                      if (filtered.length === 0) return "All";
                      const maxDisplayNames = 2;
                      const displayNames = filtered.slice(0, maxDisplayNames);
                      const remainingCount = filtered.length - maxDisplayNames;
                      const displayText =
                        remainingCount > 0
                          ? `${displayNames.join(", ")}...`
                          : displayNames.join(", ");
                      const allNames = filtered;
                      if (remainingCount > 0) {
                        return (
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  "&::-webkit-scrollbar": {
                                    width: "6px",
                                  },
                                  "&::-webkit-scrollbar-track": {
                                    bgcolor: "rgba(0, 0, 0, 0.1)",
                                    borderRadius: 1,
                                  },
                                  "&::-webkit-scrollbar-thumb": {
                                    bgcolor: "rgba(0, 0, 0, 0.3)",
                                    borderRadius: 1,
                                    "&:hover": {
                                      bgcolor: "rgba(0, 0, 0, 0.5)",
                                    },
                                  },
                                }}
                              >
                                {allNames.map((name, index) => (
                                  <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                      display: "block",
                                      py: 0.25,
                                      color: "inherit",
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: "300px",
                                  bgcolor: "rgba(97, 97, 97, 0.95)",
                                },
                              },
                            }}
                          >
                            <span
                              style={{ display: "inline-block", width: "100%" }}
                            >
                              {displayText}
                            </span>
                          </Tooltip>
                        );
                      }
                      return displayText;
                    }}
                  >
                    <MenuItem
                      sx={{
                        height: "30px",
                        margin: "2px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              woDocStatuses.length ===
                                selectedWoDocStatusIds.length &&
                              woDocStatuses.length > 0 &&
                              !selectedWoDocStatusIds.includes("0")
                            }
                            indeterminate={
                              selectedWoDocStatusIds.length > 0 &&
                              selectedWoDocStatusIds.length <
                                woDocStatuses.length &&
                              !selectedWoDocStatusIds.includes("0")
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              if (
                                woDocStatuses.length ===
                                  selectedWoDocStatusIds.length &&
                                !selectedWoDocStatusIds.includes("0")
                              ) {
                                setSelectedWoDocStatusIds(["0"]);
                              } else {
                                setSelectedWoDocStatusIds([...woDocStatuses]);
                              }
                            }}
                            sx={{
                              padding: "4px",
                            }}
                          />
                        }
                        label={
                          woDocStatuses.length ===
                            selectedWoDocStatusIds.length &&
                          woDocStatuses.length > 0 &&
                          !selectedWoDocStatusIds.includes("0")
                            ? "Unselect All"
                            : "Select All"
                        }
                        sx={{
                          margin: 0,
                          "& .MuiFormControlLabel-label": {
                            fontSize: "12px",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </MenuItem>
                    {woDocStatuses.map((docStatus) => (
                      <MenuItem
                        key={docStatus}
                        value={docStatus}
                        sx={{
                          height: "30px",
                          margin: "2px",
                        }}
                      >
                        <Checkbox
                          checked={selectedWoDocStatusIds.includes(docStatus)}
                        />
                        <ListItemText primary={docStatus} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Reset Filters Button */}
                <Tooltip title="Reset Filters" placement="top">
                  <IconButton
                    onClick={handleResetFilters}
                    size="small"
                    sx={resetFiltersButtonStyles}
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* Collapse / Expand schedule item cards */}
                <Tooltip
                  title={
                    collapseScheduleItems
                      ? "Expand Schedule Items"
                      : "Collapse Schedule Items"
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    onClick={() =>
                      startCollapseTransition(() =>
                        setCollapseScheduleItems(!collapseScheduleItems),
                      )
                    }
                    size="small"
                    sx={actionIconButton(theme, collapseScheduleItems)}
                  >
                    <img
                      src={
                        collapseScheduleItems
                          ? "/collapseWO.png"
                          : "/expandWO.png"
                      }
                      alt={
                        collapseScheduleItems ? "Collapsed" : "Expanded"
                      }
                      style={{
                        width: "20px",
                        height: "20px",
                        filter: collapseScheduleItems
                          ? "brightness(0) saturate(100%) invert(27%) sepia(91%) saturate(2878%) hue-rotate(212deg) brightness(104%) contrast(97%)"
                          : theme.palette.mode === "dark"
                            ? "invert(1) grayscale(100%) opacity(0.7)"
                            : "grayscale(100%) opacity(0.6)",
                        transition: "filter 0.2s ease",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Box sx={scheduleItemViewFilterContainerStyles}>
                {/*   <Typography
                    component="span"
                    sx={scheduleItemViewFilterLabelStyles}
                  >
                    Show
                  </Typography>
                  */}
                  <Box
                    role="group"
                    aria-label="Schedule item view"
                    sx={scheduleItemViewFilterGroupStyles(theme)}
                  >
                    {SCHEDULE_ITEM_VIEW_OPTIONS.map(({ value, label, Icon }) => {
                      const isSelected = scheduleItemView === value;
                      return (
                        <Tooltip key={value} title={label} arrow placement="top">
                          <ButtonBase
                            disableRipple
                            aria-pressed={isSelected}
                            onClick={() => setScheduleItemView(value)}
                            sx={scheduleItemViewFilterOptionStyles(
                              theme,
                              value,
                              isSelected,
                            )}
                          >
                            <Icon />
                            {label}
                          </ButtonBase>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                <Tooltip title="Reset Cache" arrow placement="top">
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
                  >
                    <IconButton
                      onClick={handleResetScheduleCache}
                      size="small"
                      disabled={
                        !onResetScheduleCache || isResettingScheduleCache
                      }
                      disableRipple
                      aria-label="Reset Cache"
                      sx={resetFiltersButtonStyles}
                    >
                      {isResettingScheduleCache ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Cached fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Tooltip>
                </Box>
                {/* Day View Controls - Date Picker and Expand/Collapse */}
                {viewMode === "day" && (
                  <Box sx={capacityBoardWeekNavGroupStyles}>
                    <RsuiteDateRangePickerWrapper />
                    <IconButton
                      onClick={handlePreviousDay}
                      size="small"
                      sx={weekNavigationButtonStyles}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <DatePicker
                      value={daySelectedDate}
                      onChange={(value) => {
                        if (!value) return;
                        handleDayDateChange(value);
                      }}
                      oneTap
                      cleanable={false}
                      format="EEEE, MMMM d, yyyy"
                      style={{ width: 280 }}
                      placement="bottomStart"
                    />
                    <IconButton
                      onClick={handleNextDay}
                      size="small"
                      sx={weekNavigationButtonStyles}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Box>
                )}

                {/* Week Navigation - hidden in Day view since day-overview has its own calendar */}
                {viewMode !== "day" && viewMode !== "month" && (
                  <Box sx={capacityBoardWeekNavGroupStyles}>
                    <Box
                      role="group"
                      aria-label="Week day range"
                      sx={scheduleItemViewFilterGroupStyles(theme)}
                    >
                      {WEEK_DAY_RANGE_OPTIONS.map(({ value, label }) => {
                        const isSelected = weekDayRange === value;
                        return (
                          <ButtonBase
                            key={value}
                            disableRipple
                            aria-pressed={isSelected}
                            onClick={() => setWeekDayRange(value)}
                            sx={weekDayRangeFilterOptionStyles(theme, isSelected)}
                          >
                            {label}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                    {!isCurrentWeek && (
                      <Box
                        component="button"
                        type="button"
                        onClick={goToCurrentWeek}
                        title="Go to current week"
                        sx={{
                          px: 1.25,
                          py: 0.5,
                          bgcolor: "background.paper",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 2,
                          color: "text.primary",
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          lineHeight: 1.2,
                          transition: "all 0.2s",
                          "&:hover": {
                            bgcolor: "action.hover",
                            borderColor: "primary.main",
                            color: "primary.main",
                          },
                        }}
                      >
                        Current Week
                      </Box>
                    )}
                    <IconButton
                      onClick={() => setWeekOffset(weekOffset - 1)}
                      title="Previous week"
                      sx={weekNavigationButtonStyles}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <Box
                      onClick={handleOpenCalendar}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "grey.800"
                            : "primary.main",
                        borderRadius: 2,
                        minWidth: 156,
                        flexShrink: 0,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? "grey.700"
                              : "primary.dark",
                          transform: "scale(1.02)",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "white",
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                        }}
                      >
                        Week of{" "}
                        {weekStart.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={() => setWeekOffset(weekOffset + 1)}
                      title="Next week"
                      sx={weekNavigationButtonStyles}
                    >
                      <ChevronRight />
                    </IconButton>
                    
                  </Box>
                )}
              </Box>
              {/* Capacity Legend */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  animation: `${scaleIn} 0.4s ease-out 0.4s both`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    fontSize: "0.7rem",
                  }}
                >
                  Capacity:
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "success.main",
                        border: 1,
                        borderColor: "divider",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      Good (≥50%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                        border: 1,
                        borderColor: "divider",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      Limited (&lt;50%)
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: "error.main",
                        border: 1,
                        borderColor: "divider",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      Full/Over
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 10,
                        borderRadius: 0.5,
                        bgcolor: (theme) => alpha(theme.palette.warning.light, 0.25),
                        border: 1,
                        borderColor: "warning.main",
                        boxShadow: (theme) => `0 0 4px ${alpha(theme.palette.warning.main, 0.4)}`,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      Past Scheduled
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Collapse>
        </Box>
      </Paper>

      {/* Board content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto", // Vertical scroll for content
          overflowX: "visible", // Allow horizontal content to extend for parent scroll
          px: 1,
          pt: 1,
          pb: 4, // Add padding bottom for space under last technician row
          bgcolor: "background.default",
          minHeight: 0, // Important for flex scrolling
          minWidth: "fit-content", // Ensure content width is respected
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "background.default",
            borderRadius: 1,
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 1,
            "&:hover": {
              bgcolor: "action.active",
            },
          },
        }}
      >
        <Box sx={{}}>
          {viewMode === "day" ? (
            <DayOverview
              referenceDate={daySelectedDate}
              onReferenceDateChange={handleDayViewDateChange}
              technicians={filteredTechnicians}
              scheduledItems={scheduledItems}
              locations={locations}
              getBaseCapacity={getBaseCapacity}
              getScheduledItemsForTechDay={getScheduledItemsForTechDay}
              onItemRemove={onItemRemove}
              onItemComplete={isScheduleReadOnly ? undefined : onItemComplete}
              readOnly={isScheduleReadOnly}
              scheduleItemView={scheduleItemView}
              onDropZone={
                isScheduleReadOnly
                  ? undefined
                  : onDropZone
                  ? (
                      e: React.DragEvent,
                      techId: number,
                      day: string,
                      date?: Date,
                    ) => {
                      // CRITICAL: Pass the date from Day view to the handler
                      // This ensures workDate is set correctly when dropping in Day view
                      const handler = onDropZone(techId, day, date);
                      handler(e);
                    }
                  : undefined
              }
              onDropAtPosition={
                isScheduleReadOnly
                  ? undefined
                  : onDropAtPosition
                  ? (
                      e: React.DragEvent,
                      techId: number,
                      day: string,
                      position: number,
                      date?: Date,
                    ) => {
                      // CRITICAL: Pass the date from Day view to the handler
                      // This ensures workDate is set correctly when dropping in Day view
                      const handler = onDropAtPosition(techId, day, date);
                      handler(e, position);
                    }
                  : undefined
              }
              onReorderItems={
                isScheduleReadOnly
                  ? undefined
                  : onReorderItems
                  ? (itemId: string, newSortKey: number) => {
                      // For day view, we need to find the item to get techId and day
                      const item = scheduledItems.find((i) => i.id === itemId);
                      if (item) {
                        const dayName = item.day;
                        onReorderItems(
                          item.technicianId,
                          dayName,
                          itemId,
                          newSortKey,
                        );
                      }
                    }
                  : undefined
              }
              workOrders={workOrders}
              segmentParts={segmentParts}
              onOpenWorkOrder={onOpenWorkOrder}
            />
          ) : viewMode === "week" ? (
            <>
              {/* Sticky header row with day labels and dates */}
              <Box
                sx={{
                  ...stickyHeaderRowStyles,
                  gridTemplateColumns: `repeat(${visibleWeekDays.length}, minmax(0, 1fr))`,
                  minWidth: 0,
                  alignItems: "start",
                }}
              >
                {/* Day headers */}
                {visibleWeekDays.map((day) => {
                  const isToday = false; // You can add logic to check if it's today
                  return (
                    <Paper
                      key={day}
                      elevation={isToday ? 3 : 1}
                      sx={{
                        ...dayHeaderPaperStyles,
                        borderColor: isToday ? "primary.dark" : "divider",
                        minWidth: 0, // Allow grid to control width
                        width: "100%", // Take full width of grid cell
                        flexShrink: 0, // Prevent shrinking
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start", // Align content to top
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          mb: 0.25,
                          fontSize: "0.75rem",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          display: "block",
                          lineHeight: 1.2,
                        }}
                      >
                        {DAY_LABELS[day]}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          display: "block",
                          lineHeight: 1.2,
                          mt: 0, // Remove any default margin to ensure alignment
                        }}
                      >
                        {dates[day]}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>

              {/* Technician rows */}
              {filteredTechnicians.map((tech) => {
                const rowData = techDayRowData.get(tech.identity);
                if (!rowData) return null;

                return (
                  <TechnicianRow
                    key={tech.identity}
                    technician={tech}
                    days={visibleWeekDays}
                    dayLabels={DAY_LABELS}
                    dates={dates}
                    dateObjects={dateObjects}
                    scheduledItems={rowData.scheduledItems}
                    availableHours={rowData.availableHours}
                    baseCapacity={rowData.baseCapacity}
                    workOrders={workOrders}
                    segmentParts={segmentParts}
                    locations={locations}
                    isLocationClosed={rowData.locationClosed}
                    isTechnicianNotWorking={rowData.technicianNotWorking}
                    onItemRemove={onItemRemove}
                    onItemsRemove={onItemsRemove}
                    onItemComplete={isScheduleReadOnly ? undefined : onItemComplete}
                    readOnly={isScheduleReadOnly}
                    scheduleItemView={scheduleItemView}
                    onDropZone={
                      isScheduleReadOnly
                        ? undefined
                        : (day) => (e, cardDate) => {
                            // Prefer date from the card (visible column) over board closure
                            const handler = onDropZone(
                              tech.identity,
                              day,
                              cardDate ?? dateObjects[day],
                            );
                            handler(e);
                          }
                    }
                    onDropAtPosition={
                      isScheduleReadOnly
                        ? undefined
                        : onDropAtPosition
                          ? (day) => (e, position, cardDate) =>
                              onDropAtPosition(
                                tech.identity,
                                day,
                                cardDate ?? dateObjects[day],
                              )(e, position)
                          : undefined
                    }
                    onReorderItems={
                      isScheduleReadOnly
                        ? undefined
                        : onReorderItems
                          ? (day, fromItemId, toSortKey) =>
                              onReorderItems(
                                tech.identity,
                                day,
                                fromItemId,
                                toSortKey,
                              )
                          : undefined
                    }
                    onUpdateItemHours={
                      isScheduleReadOnly ? undefined : onUpdateItemHours
                    }
                    onOpenWorkOrder={onOpenWorkOrder}
                    onOpenSmartWo={onOpenSmartWo}
                  />
                );
              })}
              {/* Spacer to ensure padding at bottom is visible and scroll stops correctly */}
              <Box
                sx={{
                  height: "32px",
                  minHeight: "32px",
                  width: "100%",
                  flexShrink: 0,
                }}
              />
            </>
          ) : viewMode === "month" ? (
            <MonthOverview
              referenceDate={monthSelectedDate}
              onReferenceDateChange={handleMonthViewDateChange}
              technicians={filteredTechnicians}
              scheduledItems={scheduledItems}
              locations={locations}
              getBaseCapacity={getBaseCapacity}
            />
          ) : null}
        </Box>
      </Box>

      {/* Week Selection Calendar Popover */}
      <Popover
        open={calendarOpen}
        anchorEl={anchorEl}
        onClose={handleCloseCalendar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
            mt: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 300,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Select Week
          </Typography>

          {/* Native date input to avoid MUI X DatePicker dependency */}
          <TextField
            label="Select a date in the week"
            type="date"
            size="small"
            value={formatDateForInput(weekSelectedDate)}
            onChange={(e) => {
              const parsed = parseDateFromInput(e.target.value);
              if (parsed) handleDateSelect(parsed);
            }}
            sx={{ width: "100%" }}
            InputLabelProps={{ shrink: true }}
          />

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "background.default",
              borderRadius: 1,
              width: "100%",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
            >
              Selected Week:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {weekStart.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}{" "}
              -{" "}
              {weekEnd.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
